import Button from 'components/controls/Button';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import { FormFieldData, useForm } from 'modules/forms';
import { usePermissions, UserPermission, WriteOn } from 'modules/permissions';
import { useCallback, useState } from 'react';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { OpportunityEntity } from 'shared/types/domain/OpportunityEntity';
import { Opportunity } from '../../../queries/view-opportunity.queries.types';
import {
   StyledActionsContainer,
   StyledObservationHeader,
   StyledActionsRow,
   StyledActionsCol
} from 'shared/styled/ObservationHeader.styled';
import CompleteDialog from './CompleteDialog';
import { IFormIsValidFunction } from 'modules/forms/types/IFormIsValidFunction';
import { Prompt } from 'react-router-dom';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { FileStatus } from 'shared/enums/FileStatus.enum';
import { AmmendOpportunityLoadingState } from '../../../enums/ammend-opportunity-loading-state.enum';
import { onLeaveNearMissMessage } from 'shared/constants/onLeaveInProgressMessages';
import { ConfirmationDialog } from 'components/layout/Dialog';
import { useTranslation } from 'react-i18next';

interface OpportunityHeaderProps {
   opportunity: Opportunity | undefined;
   onSave: (
      formData: FormFieldData<OpportunityEntity> | undefined,
      fileData: ObservationFileEntity[] | undefined,
      isValid: IFormIsValidFunction
   ) => void;
   onComplete: () => void;
   onReOpen: () => void;
   setLoadingState: (loadingMessage: AmmendOpportunityLoadingState, isLoading: boolean) => void;
   isLoading: boolean;
   files: ObservationFileEntity[];
}

const OpportunityHeader = ({
   opportunity,
   onSave,
   onComplete,
   onReOpen,
   setLoadingState,
   isLoading,
   files
}: OpportunityHeaderProps) => {
   const getTitle = (): string => {
      if (opportunity?.whatHappened) {
         if (opportunity.whatHappened.length > 100) {
            return `${opportunity.whatHappened.substring(0, 100)}...`;
         }

         return opportunity.whatHappened;
      }

      return 'Opportunity Loading...';
   };

   const [completeFormOpen, setCompleteFormOpen] = useState<boolean>(false);
   const [showReOpenForm, setShowReOpenForm] = useState<boolean>(false);

   const { formData, isValid } = useForm<OpportunityEntity>();
   const { hasPermission } = usePermissions();

   const hasChanges = useCallback((): boolean => {
      var dataChanged =
         opportunity?.reachId != formData?.reachId ||
         opportunity?.processId != formData?.processId ||
         opportunity?.depotPcId !== formData?.depotPcId ||
         opportunity?.whatHappened !== formData?.whatHappened ||
         opportunity?.whoShouldBeCommended !== formData?.whoShouldBeCommended ||
         opportunity?.reporterType !== formData?.reporterType ||
         opportunity?.anonymousEntry !== formData?.anonymousEntry ||
         opportunity?.reportedBy?.reporterContactName !== formData?.reporterContactName ||
         opportunity?.reportedBy?.reporterPhoneNumber !== formData?.reporterPhoneNumber ||
         opportunity?.reportedBy?.reporterEmail !== formData?.reporterEmail ||
         files?.filter(f => f.fileStatus === FileStatus.Removed && f.type?.id !== 11).length > 0 ||
         opportunity?.incidentDate !== formData?.incidentDate;

      if (dataChanged) {
         window.onbeforeunload = () => true;
      } else {
         window.onbeforeunload = null;
      }
      return dataChanged;
   }, [opportunity, formData, files]);

   const canSave = useCallback((): boolean => {
      return (
         isStillOpen(opportunity) &&
         (hasPermission(WriteOn(UserPermission.ExistingObservation)) ||
            hasPermission(WriteOn(UserPermission.IncidentDate)) ||
            hasPermission(WriteOn(UserPermission.ObservationReporter)))
      );
   }, [formData, opportunity, hasPermission]);

   const canComplete = useCallback((): boolean => {
      return isStillOpen(opportunity) && hasPermission(WriteOn(UserPermission.Completion));
   }, [formData, opportunity, hasPermission]);

   const canReOpen = useCallback((): boolean => {
      return !isStillOpen(opportunity) && hasPermission(WriteOn(UserPermission.ReOpen));
   }, [formData, opportunity, hasPermission]);

   const saveNearMiss = (): void => {
      onSave(
         formData,
         files.filter(x => x.type?.id !== 11),
         isValid
      );
   };

   const completeOpportunity = (): void => {
      setCompleteFormOpen(false);
      onComplete();
   };

   const reOpen = (): void => {
      setShowReOpenForm(false);
      onReOpen();
   };

   const closeAndResetCompletionForm = (): void => {
      setCompleteFormOpen(false);
   };

   const reOpenText = useCallback((): string => {
      if (opportunity?.observationStatus == ObservationStatusEnum.Archived) return 'Unarchive';
      if (opportunity?.observationStatus == ObservationStatusEnum.Closed) return 'Reopen';

      return '';
   }, [opportunity]);

   const reOpenConfirmationText = useCallback((): string => {
      if (opportunity?.observationStatus == ObservationStatusEnum.Archived)
         return 'Retrieving this record from archive means it will be re-opened, do you want to continue?';
      if (opportunity?.observationStatus == ObservationStatusEnum.Closed) return "Are you sure you want to re-open this Opportunity Report?";
      return '';
   }, [opportunity]);

   return (
      <StyledObservationHeader>
         <Prompt when={hasChanges()} message={'You have unsaved changes to the current near miss, are you sure you wish to navigate away?'} />
         <h1>{getTitle()}</h1>
         <StyledActionsContainer fluid>
            <StyledActionsRow>
               <StyledActionsCol xs={12} style={{ justifyContent: 'flex-end' }}>
                  <ConditionalRender condition={canSave}>
                     <Button
                        isLoading={isLoading}
                        text={'Save Changes'}
                        onClick={saveNearMiss}
                        iconName='Save'
                        disabled={!(isValid(false) && hasChanges())}
                     />
                  </ConditionalRender>

                  <ConditionalRender condition={canComplete}>
                     <CompleteDialog
                        opportunity={opportunity}
                        isOpen={completeFormOpen}
                        setLoadingState={setLoadingState}
                        onDismiss={closeAndResetCompletionForm}
                        onComplete={completeOpportunity}
                     />

                     <Button
                        style={{ marginLeft: '15px' }}
                        isLoading={isLoading}
                        text={'Complete and Action'}
                        onClick={() => setCompleteFormOpen(true)}
                        iconName='CheckMark'
                        disabled={hasChanges()}
                     />
                  </ConditionalRender>

                  <ConditionalRender condition={canReOpen}>
                     <ConfirmationDialog
                        show={showReOpenForm}
                        hidden={true}
                        title={`${reOpenText()} Opportunity`}
                        subText={reOpenConfirmationText()}
                        onDecline={() => setShowReOpenForm(false)}
                        onAccept={reOpen}
                        acceptButtonText='Proceed'
                        declineButtonText='Cancel'
                     />

                     <Button
                        style={{ marginLeft: '15px' }}
                        isLoading={isLoading}
                        text={reOpenText()}
                        onClick={() => setShowReOpenForm(true)}
                        iconName='Undo'
                        disabled={hasChanges()}
                     />
                  </ConditionalRender>
               </StyledActionsCol>
            </StyledActionsRow>
         </StyledActionsContainer>
      </StyledObservationHeader>
   );
};

export default OpportunityHeader;

function isStillOpen(nearMiss: Opportunity | undefined): boolean {
   return nearMiss?.observationStatus == ObservationStatusEnum.Open;
}
